'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '../lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error && data) {
        setProfile(data);
        return data;
      }
    } catch {}
    return null;
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Email/Password Sign Up — stores extra metadata for trigger
  const signUp = async (
    email: string,
    password: string,
    metadata: {
      fullName?: string;
      mobile?: string;
      gymName?: string;
      profilePhoto?: string;
    } = {}
  ) => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata.fullName || '',
          mobile: metadata.mobile || '',
          gym_name: 'MuscleStation',
          profile_photo: metadata.profilePhoto || '',
          role: 'member',
        },
        emailRedirectTo: `${siteUrl}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (identifier: string, password: string) => {
    let email = identifier.trim();

    // If identifier looks like a phone number, look up the email from user_profiles
    const isMobile = /^[0-9]{10,15}$/.test(identifier.replace(/[\s\-\+\(\)]/g, ''));
    if (isMobile) {
      const normalised = identifier.replace(/[\s\-\+\(\)]/g, '');
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('email')
        .eq('mobile', normalised)
        .maybeSingle();

      if (profileError || !profileData?.email) {
        throw new Error('No account found with this mobile number.');
      }
      email = profileData.email;
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    if (data.user) {
      const prof = await fetchProfile(data.user.id);
      return { ...data, profile: prof };
    }
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  // Get Current User
  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  // Refresh profile from DB
  const refreshProfile = async () => {
    if (!user) return null;
    return fetchProfile(user.id);
  };

  const isAdmin = profile?.role === 'admin';
  const isMember = profile?.role === 'member';
  const memberStatus = profile?.status ?? null;

  const value = {
    user,
    session,
    profile,
    loading,
    isAdmin,
    isMember,
    memberStatus,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    getUserProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
